import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Lock, FileCheck, Server, Users, Clock, Check, ArrowRight, Building2, Activity } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const features = [
  {
    icon: Lock,
    title: "Encryption for ePHI in transit",
    description: "WireGuard's ChaCha20 encryption protects electronic Protected Health Information as it moves across networks, supporting HIPAA's transmission-security safeguard."
  },
  {
    icon: FileCheck,
    title: "Centralized device management",
    description: "Manage devices and access from one dashboard. See which devices are provisioned and revoke them instantly, without storing patient data."
  },
  {
    icon: Server,
    title: "Secure remote access",
    description: "Enable clinicians, nurses, and staff to securely reach EHR systems, patient records, and medical applications from any location."
  },
  {
    icon: Shield,
    title: "Authenticated device access",
    description: "Every device authenticates before it reaches the network, helping guard against unauthorized access to sensitive healthcare systems."
  },
  {
    icon: Users,
    title: "Multi-site connectivity",
    description: "Connect hospitals, clinics, labs, and remote offices with encrypted site-to-site VPN tunnels."
  },
  {
    icon: Activity,
    title: "Always-on connections",
    description: "Keep clinical staff connected with persistent WireGuard tunnels that reconnect quickly across networks."
  }
];

const useCases = [
  "Telehealth and remote patient consultations",
  "EHR/EMR remote access for clinicians",
  "Medical imaging transfer between facilities",
  "Lab result sharing across locations",
  "Remote work for administrative staff",
  "Encrypted email and messaging in transit",
  "Insurance claim processing",
  "Patient portal backend security"
];

const compliancePoints = [
  { title: "Access control", desc: "Device-level authentication and authorization" },
  { title: "Audit support", desc: "Device provisioning records for your audits" },
  { title: "Integrity controls", desc: "Data encryption in transit" },
  { title: "Transmission security", desc: "Encrypted connections inside the tunnel" }
];

// Practice-specific guides. Healthcare covers hospitals, clinics, and multi-site
// organizations broadly; these are the dedicated small-practice guides so we
// stop competing with our own /business/:slug pages for the same searches.
const relatedGuides = [
  { to: "/business/dental", label: "Dental practices", desc: "Front-desk, imaging, and remote billing workflows." },
  { to: "/business/medical-spa", label: "Medical spas", desc: "Patient photos, intake forms, and payment data." },
  { to: "/business/veterinary", label: "Veterinary practices", desc: "Records, imaging, and mobile vet work." },
  { to: "/blog/hipaa-vpn-requirements-healthcare", label: "HIPAA VPN requirements guide", desc: "A deeper look at the technical safeguards and vendor questions to ask." },
];

const faqs = [
  {
    q: "Is SACVPN HIPAA compliant?",
    a: "No VPN by itself makes an organization HIPAA compliant. SACVPN's WireGuard encryption supports the Security Rule's transmission-security safeguard for data in transit, but full compliance also depends on your access controls, audit logging, workforce training, and the other administrative and physical safeguards you run across your organization."
  },
  {
    q: "Does SACVPN sign a Business Associate Agreement (BAA)?",
    a: "Not yet, and we would rather say so plainly than promise paperwork we cannot back up today. A BAA is actively in progress: the agreement itself is drafted from the HHS model provisions, and we are completing the upstream agreements with our infrastructure providers first, because a BAA is only meaningful if the whole chain is covered. If a signed BAA is a requirement for your organization, tell us and we will let you know the moment it is available."
  },
  {
    q: "Can SACVPN protect telehealth sessions?",
    a: "Yes. SACVPN adds an encrypted layer underneath your telehealth platform's own security, so the connection between the clinician's device and our server stays protected on hotel, home, or public Wi-Fi."
  },
  {
    q: "How does SACVPN help when we have multiple clinics or hospital locations?",
    a: "Each site connects through SACVPN over a shared, encrypted path, so staff at any clinic or hospital location reach your EHR and internal systems the same secure way, whether they are on-site or remote."
  },
  {
    q: "What's the difference between this page and the dental, medical spa, or veterinary guides?",
    a: "This page covers VPN use across hospitals, clinics, and multi-site healthcare organizations generally. If you run a specific type of practice, our dedicated guides for dental, medical spa, and veterinary practices cover the exact workflows and setup notes for your office."
  }
];

export default function HealthcareVPN() {
  return (
    <>
      <Helmet>
        <title>VPN for Healthcare | Secure Medical Remote Access | SACVPN</title>
        <meta
          name="description"
          content="Secure VPN for healthcare organizations. WireGuard encryption for ePHI in transit, EHR remote access, telehealth security, and multi-site connectivity."
        />
        <meta
          name="keywords"
          content="healthcare VPN, medical VPN, hospital VPN, clinic VPN, EHR VPN, telehealth security, healthcare cybersecurity, ePHI protection, secure remote access"
        />
        <link rel="canonical" href="https://www.sacvpn.com/industries/healthcare" />
        <meta property="og:title" content="VPN for Healthcare | Secure Medical Remote Access | SACVPN" />
        <meta property="og:description" content="Secure VPN for healthcare organizations. WireGuard encryption for ePHI in transit, EHR remote access, telehealth security, and multi-site connectivity." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Healthcare VPN Service",
            "name": "SACVPN Healthcare VPN Solutions",
            "description": "WireGuard VPN service for healthcare organizations including hospitals, clinics, and medical practices.",
            "provider": {
              "@type": "Organization",
              "name": "SACVPN"
            },
            "areaServed": {
              "@type": "Country",
              "name": "United States"
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Healthcare Organizations"
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map((f) => ({
              "@type": "Question",
              "name": f.q,
              "acceptedAnswer": { "@type": "Answer", "text": f.a }
            }))
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-dark-900" />
        <div className="absolute inset-0 bg-[radial-gradient(900px_460px_at_78%_20%,rgba(14,165,233,0.14),transparent_60%)]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="grid lg:grid-cols-2 gap-12 items-center"
          >
            <div>
              <motion.p variants={fadeUp} className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">
                Healthcare
              </motion.p>

              <motion.h1
                variants={fadeUp}
                className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-white leading-[1.05] tracking-tight"
              >
                Secure VPN for{" "}
                <span className="text-brand-400">healthcare</span>
              </motion.h1>

              <motion.p
                variants={fadeUp}
                className="mt-6 text-lg text-gray-400 leading-relaxed max-w-xl"
              >
                Protect patient data in transit and enable secure remote access for your organization. WireGuard encryption, telehealth security, and multi-site connectivity built for hospitals, clinics, and medical practices.
              </motion.p>

              <motion.div variants={fadeUp} className="mt-9 flex flex-wrap gap-3">
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-brand-500 text-white font-semibold hover:bg-brand-400 transition-colors"
                >
                  <Clock className="w-5 h-5" />
                  Start free trial
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg border border-white/15 text-white font-semibold hover:bg-white/5 transition-colors"
                >
                  Request demo
                </Link>
              </motion.div>
            </div>

            <motion.div variants={fadeUp} className="relative">
              <div className="rounded-xl border border-white/[0.08] bg-dark-800/40 p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-brand-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Healthcare security</h3>
                    <p className="text-gray-500 text-sm">Encryption for data in transit</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {["WireGuard (ChaCha20) encryption", "Supports HIPAA technical safeguards", "No-logs policy", "Authenticated device access"].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 text-gray-400">
                      <Check className="w-4 h-4 text-brand-400 flex-shrink-0" />
                      <span className="text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* HIPAA Safeguards Section */}
      <section className="py-24 bg-dark-900">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="max-w-2xl mb-14"
          >
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">Safeguards</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">
              Supporting HIPAA technical safeguards
            </h2>
            <p className="mt-4 text-gray-400 leading-relaxed">
              SACVPN encrypts connections in transit, which supports the technical safeguards in the HIPAA Security Rule. Overall HIPAA compliance is a program you run across your whole organization.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {compliancePoints.map((point, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                className="rounded-xl border border-white/[0.08] bg-dark-900 p-6 hover:border-brand-500/30 transition-colors"
              >
                <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center mb-5">
                  <Check className="w-5 h-5 text-brand-400" />
                </div>
                <h3 className="font-semibold text-white mb-2">{point.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{point.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-dark-800/40 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="max-w-2xl mb-14"
          >
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">Capabilities</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">
              Built for healthcare operations
            </h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.06] border border-white/[0.06] rounded-xl overflow-hidden"
          >
            {features.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  className="bg-dark-900 p-8 hover:bg-dark-800 transition-colors"
                >
                  <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-brand-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-24 bg-dark-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
            >
              <motion.p variants={fadeUp} className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">
                Use cases
              </motion.p>
              <motion.h2
                variants={fadeUp}
                className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight mb-6"
              >
                Healthcare use cases
              </motion.h2>
              <motion.p
                variants={fadeUp}
                className="text-lg text-gray-400 mb-8 leading-relaxed"
              >
                From telehealth to EHR access, SACVPN secures the connections across your healthcare IT.
              </motion.p>
              <motion.div
                variants={fadeUp}
                className="grid grid-cols-1 sm:grid-cols-2 gap-4"
              >
                {useCases.map((useCase, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-400">{useCase}</span>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="rounded-xl border border-white/[0.08] bg-dark-800/40 p-8"
            >
              <h3 className="text-xl font-semibold text-white mb-6">Why healthcare teams choose SACVPN</h3>
              <div className="space-y-4">
                {[
                  "Deploy in minutes, not weeks",
                  "No hardware to install or maintain",
                  "Per-device pricing scales with your organization",
                  "Dedicated account manager for Enterprise plans"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-gray-400">
                    <ArrowRight className="w-5 h-5 text-brand-400" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Practice-Specific Guides Section */}
      <section className="py-24 bg-dark-800/40 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="max-w-2xl mb-14"
          >
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">Find your fit</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">
              Run a specific type of practice?
            </h2>
            <p className="mt-4 text-gray-400 leading-relaxed">
              This page covers VPN use across hospitals, clinics, and multi-site healthcare organizations generally. If you run a smaller practice, see the dedicated guide below for the exact workflows and setup notes.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4"
          >
            {relatedGuides.map((guide, i) => (
              <motion.div key={i} variants={fadeUp}>
                <Link
                  to={guide.to}
                  className="group flex flex-col h-full rounded-xl border border-white/[0.08] bg-dark-900 p-5 hover:border-brand-500/30 transition-colors"
                >
                  <span className="font-semibold text-white group-hover:text-brand-400 transition-colors">
                    {guide.label}
                  </span>
                  <span className="mt-1 text-sm text-gray-500 flex-1">{guide.desc}</span>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-sm text-brand-400 font-medium">
                    Read the guide <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-dark-900">
        <div className="max-w-3xl mx-auto px-6">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight mb-10 text-center"
          >
            Healthcare VPN questions, answered
          </motion.h2>
          <div className="space-y-4">
            {faqs.map((f, i) => (
              <details key={i} className="group bg-dark-800/40 rounded-2xl border border-white/[0.08] p-6">
                <summary className="flex items-center justify-between cursor-pointer list-none font-semibold text-white">
                  <span>{f.q}</span>
                  <ArrowRight className="w-5 h-5 text-gray-400 group-open:rotate-90 transition-transform flex-shrink-0 ml-4" />
                </summary>
                <p className="text-gray-400 mt-4 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-dark-900 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="relative overflow-hidden rounded-2xl border border-brand-500/20 bg-[radial-gradient(700px_320px_at_50%_-10%,rgba(14,165,233,0.18),transparent_70%)] bg-dark-800 px-8 md:px-14 py-16 text-center"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight mb-4">
              Protect your patients' data today
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-9">
              Secure your organization's connections with WireGuard encryption. Start your 14-day free trial.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg bg-brand-500 text-white font-semibold hover:bg-brand-400 transition-colors"
              >
                <Clock className="w-5 h-5" />
                Start free trial
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg border border-white/15 text-white font-semibold hover:bg-white/5 transition-colors"
              >
                Contact sales
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
